import { useState, useEffect } from 'react'
import { backfillService } from '../services/backfillService'
import { CATEGORY_OPTIONS, CATEGORY_DISPLAY_NAMES, CATEGORY_COLORS } from '../types'

interface BackfillCategoryStepProps {
  onSelect: (category: string) => void
}

export function BackfillCategoryStep({ onSelect }: BackfillCategoryStepProps) {
  const [counts, setCounts] = useState<Record<string, number>>({})
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const fetchCounts = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const data = await backfillService.getClientCountByCategory()
        if (!cancelled) setCounts(data)
      } catch {
        if (!cancelled) setError('Error al obtener conteo de clientes')
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    fetchCounts()
    return () => { cancelled = true }
  }, [])

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {CATEGORY_OPTIONS.map((cat) => (
          <div key={cat} className="animate-pulse h-40 bg-gray-100 rounded-lg" />
        ))}
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">
        {error}
      </div>
    )
  }

  return (
    <div>
      <p className="text-gray-600 mb-6">
        Selecciona el nivel de clientes al que deseas asignar beneficios retroactivamente.
        Solo se asignarán beneficios del nivel seleccionado.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {CATEGORY_OPTIONS.map((cat) => {
          const colors = CATEGORY_COLORS[cat]
          const count = counts[cat] ?? 0
          return (
            <button
              key={cat}
              onClick={() => onSelect(cat)}
              className={`${colors.bg} ${colors.border} border-2 rounded-xl p-6 text-left transition-all hover:shadow-lg hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-primary-500`}
            >
              <h3 className={`text-2xl font-bold ${colors.text}`}>
                {CATEGORY_DISPLAY_NAMES[cat] ?? cat}
              </h3>
              <p className="text-3xl font-semibold text-gray-900 mt-3">
                {count.toLocaleString('es-CL')}
              </p>
              <p className="text-sm text-gray-500 mt-1">clientes en este nivel</p>
            </button>
          )
        })}
      </div>
    </div>
  )
}
